import React, { createContext, useContext, useEffect } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../services/firebase.config';
import { useAuthStore } from '../stores/authStore';
import { UserProfile } from '../types';

interface AuthContextType {
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Demo user profiles - COMMENTATO PER USO FUTURO
// const getDemoUserProfile = (demoUserId: string, userEmail: string, userName: string): UserProfile => {
//   const baseProfile = {
//     email: userEmail,
//     name: userName,
//     createdAt: Timestamp.now(),
//     preferences: {
//       emailNotifications: true,
//       pushNotifications: true
//     }
//   };

//   switch (demoUserId) {
//     case 'admin':
//       return {
//         ...baseProfile,
//         email: 'admin@demo.com',
//         name: 'Mario Rossi',
//         role: 'admin',
//         department: 'Management',
//         joinDate: Timestamp.fromDate(new Date('2023-01-15')),
//         permissions: ['view_all', 'manage_users', 'admin_panel']
//       };
//     case 'user1':
//       return {
//         ...baseProfile,
//         email: 'user1@demo.com',
//         name: 'Giulia Bianchi',
//         role: 'user',
//         department: 'Development',
//         joinDate: Timestamp.fromDate(new Date('2023-06-20')),
//         permissions: ['view_own', 'create_applications']
//       };
//     case 'user2':
//       return {
//         ...baseProfile,
//         email: 'user2@demo.com',
//         name: 'Luca Verdi',
//         role: 'user',
//         department: 'Marketing',
//         joinDate: Timestamp.fromDate(new Date('2024-02-10')),
//         permissions: ['view_own', 'create_applications']
//       };
//     default:
//       return baseProfile;
//   }
// };

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { setUser, setUserProfile, setLoading, setError } = useAuthStore();

  useEffect(() => {
    console.log('🔐 Inizializzazione AuthProvider...');

    // Modalità Firebase reale
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        console.log('👤 Auth state changed:', user ? 'Utente loggato' : 'Nessun utente');
        setUser(user);
        
        if (user) {
          console.log('🔥 Modalità FIREBASE: Recupero profilo da Firestore');
          try {
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
            
            if (userDoc.exists()) {
              const profile = userDoc.data() as UserProfile;
              console.log('📋 Dati grezzi da Firestore:', userDoc.data());
              setUserProfile(profile);
              console.log('✅ Profilo utente caricato da Firestore:', {
                uid: profile.uid,
                email: profile.email,
                role: profile.role,
                name: profile.name
              });
            } else {
              console.log('📝 Creazione nuovo profilo utente in Firestore');
              const newProfile: UserProfile = {
                uid: user.uid,
                email: user.email || '',
                name: user.displayName || '',
                role: 'user',
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                preferences: {
                  emailNotifications: true,
                  pushNotifications: true
                }
              };
              
              await setDoc(userDocRef, newProfile);
              setUserProfile(newProfile);
              console.log('✅ Nuovo profilo creato in Firestore');
            }
          } catch (firestoreError) {
            console.warn('⚠️ Errore Firestore, usando profilo fallback:', firestoreError);
            const fallbackProfile: UserProfile = {
              uid: user.uid,
              email: user.email || '',
              name: user.displayName || 'Utente',
              role: 'user',
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
              preferences: {
                emailNotifications: true,
                pushNotifications: true
              }
            };
            setUserProfile(fallbackProfile);
            setError('Connessione al database limitata. Alcune funzionalità potrebbero non essere disponibili.');
          }
        } else {
          setUserProfile(null);
          console.log('👋 Utente disconnesso');
        }
      } catch (error) {
        console.error('❌ Errore durante cambio stato autenticazione:', error);
        setError(error instanceof Error ? error.message : 'Errore di autenticazione sconosciuto');
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [setUser, setUserProfile, setLoading, setError]);

  const signOut = async () => {
    try {
      console.log('👋 Logout in corso...');
      await firebaseSignOut(auth);
      console.log('✅ Logout completato');
    } catch (error) {
      console.error('❌ Errore durante il logout:', error);
      setError(error instanceof Error ? error.message : 'Errore durante il logout');
    }
  };

  const value: AuthContextType = {
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 